/**
 * File components/BookingForm.js
 *
 * To display a formulaire to send contact and booking informations per email
 *
 * Learn more: https://saint-augustin.ch
 */

import {useState} from 'react';
import {useBookingDataContext} from '../context/BookingDataProvider';

import "../styles/BookingForm.css"

const BookingForm = () => {
  const {
    beginBooking, setBeginBooking,
    endBooking, setEndBooking,
    locations,
    nbPersons, setNbPersons,
    disponibilityStatus, setDisponibilityStatus
  } = useBookingDataContext();

  const [formData, setFormData] = useState({
    contact: {
      firstName: '',
      lastName: '',
      contactType: 'privateContact',
      companyName: '',
      street: '',
      postalCode: '',
      city: '',
      phone: '',
      email: '',
    },
    description: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name in formData.contact) {
      setFormData({
        ...formData,
        contact: {
          ...formData.contact,
          [name]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

// Create booking event
async function createGoogleEvent({ calendarId, start, end, summary }) {
  const response = await fetch('/wp-json/booking/v1/create-event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      calendarId,
      start,
      end,
      summary,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    console.error("Erreur API:", data);
    throw new Error(data?.message || 'Erreur API');
  }

  return data; // { success: true, eventId: "xxx" }
}

  // Sending booking request
  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    let bookingText = `Détails de la réservation\r\n\r\n`;
    bookingText += `Nom : ${formData.contact.firstName} ${formData.contact.lastName}\r\n`;
    bookingText += formData.contact.contactType==='companyContact'?`Enteprise : ${formData.contact.companyName}\r\n`:``;
    bookingText += `Adresse : ${formData.contact.street}, ${formData.contact.postalCode} ${formData.contact.city}\r\n`;
    bookingText += `Téléphone : ${formData.contact.phone}\r\n`;
    bookingText += `Email : ${formData.contact.email}\r\n`;
    bookingText += `Période : du ${beginBooking} au ${endBooking}\r\n`;
    bookingText += `Nombre de personnes : ${nbPersons}\r\n\r\n`;

    bookingText += `Lieux et équipements sélectionnés :\r\n`;

    try {
      for (const loc of locations.filter(l => l.isSelected)) {
        const selectedEquipments = loc.equipments.filter(eq => eq.isSelected);

        // Ajouter à bookingText
        bookingText += `- ${loc.title.rendered} – ${loc.building_name}\r\n`;
        selectedEquipments.forEach(eq => {
          bookingText += `   • ${eq.name} – CHF ${eq.price} ${
            eq.price_type === 'pro_person' ? '/personne' : eq.price_type === 'pro_hour' ? '/heure' : ''
          }\r\n`;
        });

        // Créer un événement provisoire sur Google Calendar
        const response = await createGoogleEvent({
          calendarId: loc.calendar_id,
          start: new Date(beginBooking).toISOString(),
          end: new Date(endBooking).toISOString(),
          summary: `Réservation provisoire – ${formData.contact.firstName} ${formData.contact.lastName}`
        });
      }

    } catch (err) {
      console.error(err);
      setError("Impossible de créer l'événement dans Google Calendar.");
    }

    bookingText += `\r\nDescription : ${formData.description}\r\n`;

    try {
      const response = await fetch('https://paroisse-catholique-saint-joseph-lausanne.ch/wp-json/booking/v1/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: 'Nouvelle demande de réservation',
          message: bookingText,
          contact: formData.contact,
        }),
      });
      console.log(response.status, response.statusText);

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi du message.');
      }

      setSuccess(true);
    } catch (err) {
      setError('Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmitBooking}>
      <div className="identity">
        <div className="form-group">
          <br/>
          <label htmlFor="firstName">Prénom:</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.contact.firstName}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
            <label htmlFor="lastName">Nom:</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.contact.lastName}
              onChange={handleChange}
              required
            />
        </div>
      </div>
      <div className="form-group">
        <label>
          <input
            type="radio"
            name="contactType"
            value="privateContact"
            checked={formData.contact.contactType === "privateContact"}
            onChange={handleChange}
          />
          Particulier
        </label>
        <label style={{ marginLeft: "1rem" }}>
          <input
            type="radio"
            name="contactType"
            value="companyContact"
            checked={formData.contact.contactType === "companyContact"}
            onChange={handleChange}
          />
          Entreprise
        </label>
        <label htmlFor="companyName">:</label>
        <input
          type="text"
          id="companyName"
          name="companyName"
          value={formData.contact.companyName}
          onChange={handleChange}
          required={formData.contact.contactType === "companyContact"}
          disabled={formData.contact.contactType === "privateContact"}
        />
      </div>
      <div>
        <label htmlFor="street">Nom de rue:</label>
        <input
          type="text"
          id="street"
          name="street"
          value={formData.contact.street}
          onChange={handleChange}
          required
        />
      </div>

      <div>
        <label htmlFor="postalCode">Code postal:</label>
        <input
          type="text"
          id="postalCode"
          name="postalCode"
          value={formData.contact.postalCode}
          onChange={handleChange}
          required
        />
      </div>

      <div>
        <label htmlFor="city">Localité:</label>
        <input
          type="text"
          id="city"
          name="city"
          value={formData.contact.city}
          onChange={handleChange}
          required
        />
      </div>

      <div>
        <label htmlFor="phone">Numéro de téléphone:</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.contact.phone}
          onChange={handleChange}
          pattern="^[0-9+\s]{6,15}$"
          required
        />
      </div>

      <div>
        <label htmlFor="email">Email:</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.contact.email}
          onChange={handleChange}
          required
        />
      </div>

      <div>
        <label htmlFor="description">Description:</label>
        <textarea
          id="description"
          name="description"
		  rows="5"
		  placeholder="Veuillez indiquer en quelques mots l'événement organisé"
          value={formData.description}
          onChange={handleChange}
        ></textarea>
      </div>
	  <p>Vous allez recevoir une confirmation de votre demande et nous vous contacterons pour préciser votre location et établir un contrat d'utilisation.</p>
			{!disponibilityStatus && 
				<div className="error"><br/><strong>Vérifiez la disponibilité avant l'envoi !</strong><br/><br/></div>
			}
      <div>
        <input type="submit" disabled={loading || !disponibilityStatus} value={loading ? 'Envoi en cours...' : 'Envoyer la réservation'} />
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}
      {success && <p style={{ color: 'green' }}>Votre demande a été envoyée avec succès !</p>}
    </form>
  );
};

export default BookingForm;